import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import supportTicketService from '../../services/supportTicketService';
import {
  ALLOWED_IMAGE_TYPES,
  MAX_EVIDENCE_BYTES,
  MAX_EVIDENCE_FILE_COUNT,
  TICKET_CATEGORIES,
  formatEvidenceSize,
  getTotalEvidenceBytes,
} from './supportTicketUtils';
import './SupportTicketPanel.css';

const SupportTicketPanel = ({ open, onClose, onSubmitted }) => {
  const [category, setCategory] = useState(TICKET_CATEGORIES[0]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [descriptionError, setDescriptionError] = useState('');
  const fileInputRef = useRef(null);

  const totalBytes = useMemo(() => getTotalEvidenceBytes(evidenceFiles), [evidenceFiles]);

  const previewUrls = useMemo(
    () => evidenceFiles.map((file) => URL.createObjectURL(file)),
    [evidenceFiles]
  );

  useEffect(() => {
    return () => {
      previewUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [previewUrls]);

  useEffect(() => {
    if (!open) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape') onClose();
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, onClose]);

  if (!open) return null;

  const resetForm = () => {
    setCategory(TICKET_CATEGORIES[0]);
    setTitle('');
    setDescription('');
    setEvidenceFiles([]);
    setDragActive(false);
    setDescriptionError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const validateFile = (file) => {
    if (!file) return false;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      toast.error(`"${file.name}" is not allowed. Use PNG, JPG, JPEG, or GIF.`);
      return false;
    }
    if (file.size > MAX_EVIDENCE_BYTES) {
      toast.error(`"${file.name}" exceeds 20MB.`);
      return false;
    }
    return true;
  };

  const mergeEvidenceFiles = (incoming = []) => {
    const next = [...evidenceFiles];
    for (const file of incoming) {
      if (!validateFile(file)) continue;
      const duplicate = next.some(
        (existing) =>
          existing.name === file.name &&
          existing.size === file.size &&
          existing.lastModified === file.lastModified
      );
      if (duplicate) continue;
      next.push(file);
    }
    if (next.length > MAX_EVIDENCE_FILE_COUNT) {
      toast.error(`You can upload up to ${MAX_EVIDENCE_FILE_COUNT} images.`);
      return next.slice(0, MAX_EVIDENCE_FILE_COUNT);
    }
    const combinedSize = getTotalEvidenceBytes(next);
    if (combinedSize > MAX_EVIDENCE_BYTES) {
      toast.error('Total size of all images must not exceed 20MB.');
      return evidenceFiles;
    }
    return next;
  };

  const handleFilesSelection = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setEvidenceFiles(mergeEvidenceFiles(files));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveFile = (index) => {
    setEvidenceFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    handleFilesSelection(event.dataTransfer.files);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const trimmedTitle = title.trim();
    const trimmedDescription = description.trim();

    if (!trimmedDescription) {
      setDescriptionError('Description is required.');
      return;
    }
    setDescriptionError('');
    if (totalBytes > MAX_EVIDENCE_BYTES) {
      toast.error('Total size of all images must not exceed 20MB.');
      return;
    }

    const formData = new FormData();
    formData.append('category', category);
    formData.append('title', trimmedTitle);
    formData.append('description', trimmedDescription);
    evidenceFiles.forEach((file) => {
      formData.append('evidence', file);
    });

    setIsSubmitting(true);
    try {
      const result = await supportTicketService.createTicket(formData);
      if (!result?.success) {
        const message = result?.message || 'Failed to submit ticket.';
        if (/description is required/i.test(message)) {
          setDescriptionError('Description is required.');
          return;
        }
        toast.error(message);
        return;
      }
      toast.success('Support ticket submitted successfully.');
      resetForm();
      onSubmitted?.(result.data);
      onClose();
    } catch (error) {
      const message = error?.response?.data?.message || error.message || 'Failed to submit ticket.';
      if (/description is required/i.test(message)) {
        setDescriptionError('Description is required.');
      } else {
        toast.error(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="support-ticket-overlay" onClick={onClose}>
      <aside
        className="support-ticket-panel"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="support-ticket-panel-title"
      >
        <header className="support-ticket-panel-header">
          <h2 id="support-ticket-panel-title">Submit Support Ticket</h2>
          <button type="button" className="support-ticket-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <form className="support-ticket-panel-body" onSubmit={handleSubmit}>
          <label className="support-ticket-field">
            <span>Category</span>
            <select value={category} onChange={(event) => setCategory(event.target.value)}>
              {TICKET_CATEGORIES.map((item) => (
                <option key={item} value={item}>{item}</option>
              ))}
            </select>
          </label>

          <label className="support-ticket-field">
            <span>Subject <span className="support-ticket-optional">(optional)</span></span>
            <input
              type="text"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Brief summary of the issue"
              maxLength={255}
            />
          </label>

          <div className="support-ticket-field">
            <label htmlFor="support-ticket-description" className="support-ticket-label">
              <span className="support-ticket-label-required">
                <span className="support-ticket-label-name">Description</span>
                <span className="support-ticket-req">*</span>

              </span>
            </label>
            <textarea
              id="support-ticket-description"
              value={description}
              onChange={(event) => {
                setDescription(event.target.value);
                if (descriptionError && event.target.value.trim()) {
                  setDescriptionError('');
                }
              }}
              rows={7}
              placeholder="Describe the issue in detail. If submitting a video, please upload it to Google Drive and paste the shareable link here."
              aria-invalid={descriptionError ? 'true' : 'false'}
              aria-describedby={descriptionError ? 'support-ticket-description-error' : undefined}
              className={descriptionError ? 'has-error' : ''}
            />
            {descriptionError ? (
              <p id="support-ticket-description-error" className="support-ticket-field-error" role="alert">
                {descriptionError}
              </p>
            ) : null}
          </div>

          <div className="support-ticket-field">
            <span>Upload images</span>
            <div
              className={`support-ticket-dropzone ${dragActive ? 'is-active' : ''}`}
              onDragEnter={(event) => {
                event.preventDefault();
                setDragActive(true);
              }}
              onDragOver={(event) => {
                event.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={(event) => {
                event.preventDefault();
                setDragActive(false);
              }}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/gif"
                className="support-ticket-file-input"
                multiple
                onChange={(event) => handleFilesSelection(event.target.files)}
              />
              <p>Drag and drop images here, or click to browse</p>
              <small>
                PNG, JPG, JPEG, GIF. Up to {MAX_EVIDENCE_FILE_COUNT} images, {formatEvidenceSize(totalBytes)} / 20 MB total.
              </small>
            </div>

            {evidenceFiles.length > 0 && (
              <ul className="support-ticket-upload-list">
                {evidenceFiles.map((file, index) => (
                  <li key={`${file.name}-${file.size}-${file.lastModified}`} className="support-ticket-upload-item">
                    <button
                      type="button"
                      className="support-ticket-upload-thumb-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        const url = previewUrls[index];
                        if (url) window.open(url, '_blank', 'noopener,noreferrer');
                      }}
                    >
                      <img
                        src={previewUrls[index]}
                        alt={file.name}
                        className="support-ticket-upload-thumb"
                      />
                    </button>
                    <div className="support-ticket-upload-meta">
                      <span className="support-ticket-upload-name">{file.name}</span>
                      <span className="support-ticket-upload-size">{formatEvidenceSize(file.size)}</span>
                    </div>
                    <button
                      type="button"
                      className="support-ticket-upload-remove"
                      aria-label={`Remove ${file.name}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFile(index);
                      }}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <footer className="support-ticket-panel-footer">
            <button type="submit" className="support-ticket-send-btn" disabled={isSubmitting}>
              {isSubmitting ? 'Sending…' : 'Send'}
            </button>
          </footer>
        </form>
      </aside>
    </div>
  );
};

export default SupportTicketPanel;
